import { useMemo } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from "recharts";
import { FileText } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { InfoHint } from "@/components/ui/info-hint";
import { KpiBasisLabel, KpiBasisNote } from "@/components/reports/KpiBasisLabel";
import { useInvoicesByMonth } from "@/hooks/useInvoices";
import { SectionLoadError } from "@/components/invoicing/SectionErrorBoundary";
import { useQueryClient } from "@tanstack/react-query";

interface InvoicesByMonthChartProps {
  tramsAccountId?: number;
  viewMode: "my" | "all";
  returnDateFrom?: string;
  returnDateTo?: string;
}

const MONTH_LABELS = [
  "JAN", "FEB", "MAR", "APR", "MAY", "JUN",
  "JUL", "AUG", "SEP", "OCT", "NOV", "DEC",
];

/**
 * AR-316: the label used to be the month name alone, so a range spanning more
 * than one calendar year produced repeated ticks (`JUL MAR JUN SEP DEC MAR JUN
 * SEP`) with no way to tell the years apart — very visible on a phone, where
 * recharts thins the ticks down to a handful of non-adjacent months. Append a
 * two-digit year whenever the series covers more than one year.
 */
function formatMonthLabel(month: string, withYear = false): string {
  const parts = month.split("-");
  const monthIdx = parseInt(parts[1], 10) - 1;
  const name = MONTH_LABELS[monthIdx] ?? month;
  if (!withYear) return name;
  const year = parts[0]?.slice(-2);
  return year ? `${name} '${year}` : name;
}

interface ChartDataPoint {
  month: string;
  label: string;
  closed: number;
  open: number;
  total: number;
}

function CustomTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: Array<{ name: string; value: number; color: string }>;
  label?: string;
}) {
  if (!active || !payload?.length) return null;

  const closed = payload.find((p) => p.name === "closed")?.value ?? 0;
  const open = payload.find((p) => p.name === "open")?.value ?? 0;
  const total = closed + open;

  return (
    <div className="rounded-lg border bg-card px-3 py-2 shadow-md text-xs">
      <p className="font-semibold text-foreground mb-1">{label}</p>
      <div className="space-y-0.5">
        <div className="flex items-center gap-2">
          <span
            className="inline-block h-2 w-2 rounded-sm"
            style={{ backgroundColor: "#ea7b51" }}
          />
          <span className="text-muted-foreground">Paid</span>
          <span className="font-semibold ml-auto">{closed}</span>
        </div>
        <div className="flex items-center gap-2">
          <span
            className="inline-block h-2 w-2 rounded-sm"
            style={{ backgroundColor: "#f5c5b3" }}
          />
          <span className="text-muted-foreground">Open</span>
          <span className="font-semibold ml-auto">{open}</span>
        </div>
        <div className="border-t pt-1 mt-1 flex items-center gap-2">
          <span className="text-muted-foreground">Total</span>
          <span className="font-semibold ml-auto">{total}</span>
        </div>
      </div>
    </div>
  );
}

export function InvoicesByMonthChart({
  tramsAccountId,
  viewMode,
  returnDateFrom,
  returnDateTo,
}: InvoicesByMonthChartProps) {
  const queryClient = useQueryClient();
  const { data: response, isLoading, error } = useInvoicesByMonth({
    tramsAccountId,
    viewMode,
    returnDateFrom,
    returnDateTo,
  });

  const chartData = useMemo<ChartDataPoint[]>(() => {
    if (!response?.data?.length) return [];
    const years = new Set(response.data.map((b) => b.month.slice(0, 4)));
    const spansMultipleYears = years.size > 1;
    return response.data.map((bucket) => ({
      month: bucket.month,
      label: formatMonthLabel(bucket.month, spansMultipleYears),
      closed: bucket.closedCount,
      open: bucket.openCount,
      total: bucket.closedCount + bucket.openCount,
    }));
  }, [response]);

  const maxValue = useMemo(() => {
    if (!chartData.length) return 12;
    const maxTotal = Math.max(...chartData.map((d) => d.total));
    return Math.ceil(maxTotal * 1.15) || 12;
  }, [chartData]);

  if (error) {
    return (
      <SectionLoadError
        sectionName="Invoices by Month"
        error={error}
        onRetry={() => {
          queryClient.invalidateQueries({
            queryKey: ["/api/invoices/invoices-by-month"],
          });
        }}
      />
    );
  }

  if (isLoading) {
    return (
      <div className="rounded-2xl bg-muted/60 p-5 flex flex-col gap-4 h-full">
        <div className="flex items-center gap-2">
          <FileText className="h-4 w-4 text-muted-foreground" />
          <Skeleton className="h-4 w-32" />
        </div>
        <div className="flex-1 rounded-xl bg-card border p-5 flex items-center justify-center">
          <Skeleton className="h-full w-full rounded-lg" />
        </div>
      </div>
    );
  }

  const isEmpty = chartData.length === 0;

  return (
    <div className="rounded-2xl bg-muted/60 p-5 flex flex-col gap-4 h-full">
      {/* Title row */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <FileText className="h-4 w-4 text-muted-foreground" />
          <h3 className="text-sm font-semibold">Invoices by Month</h3>
          <InfoHint label="How invoices by month is calculated">
            <p>
              See your monthly departures and whether the invoice has been
              closed (paid) or is still open. Great for spotting upcoming
              travel, seasonal trends, and payment patterns.
            </p>
            <KpiBasisNote card="invoicedByMonth" />
          </InfoHint>
          <KpiBasisLabel card="invoicedByMonth" />
        </div>
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          <span className="flex items-center gap-1">
            <span
              className="inline-block h-2.5 w-2.5 rounded-sm"
              style={{ backgroundColor: "#ea7b51" }}
            />
            Paid
          </span>
          <span className="flex items-center gap-1">
            <span
              className="inline-block h-2.5 w-2.5 rounded-sm"
              style={{ backgroundColor: "#f5c5b3" }}
            />
            Open
          </span>
        </div>
      </div>

      {/* AR-210 (TC-13.2): text alternative for the bar chart. */}
      {!isEmpty && (
        <table className="sr-only">
          <caption>Invoices by month</caption>
          <thead>
            <tr>
              <th scope="col">Month</th>
              <th scope="col">Paid</th>
              <th scope="col">Open</th>
            </tr>
          </thead>
          <tbody>
            {chartData.map((d) => (
              <tr key={d.month}>
                <th scope="row">{d.label}</th>
                <td>{d.closed}</td>
                <td>{d.open}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {/* Chart area */}
      <div className="flex-1 rounded-xl bg-card border px-3 py-4 min-h-[200px]" aria-hidden={isEmpty ? undefined : true}>
        {isEmpty ? (
          <div className="h-full flex items-center justify-center">
            <div className="text-center space-y-2">
              <FileText className="h-8 w-8 text-muted-foreground/20 mx-auto" />
              <p className="text-xs text-muted-foreground max-w-[240px]">
                No invoices with departure dates found.
              </p>
            </div>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={chartData}
              margin={{ top: 4, right: 8, left: -12, bottom: 0 }}
              barCategoryGap="40%"
              maxBarSize={32}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                vertical={false}
                stroke="hsl(var(--border))"
              />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                domain={[0, maxValue]}
                allowDecimals={false}
                tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }}
                axisLine={false}
                tickLine={false}
              />
              <RechartsTooltip
                content={<CustomTooltip />}
                cursor={{ fill: "hsl(var(--muted))", opacity: 0.4 }}
              />
              <Bar
                dataKey="closed"
                name="closed"
                stackId="invoices"
                fill="#ea7b51"
                radius={[0, 0, 0, 0]}
              />
              <Bar
                dataKey="open"
                name="open"
                stackId="invoices"
                fill="#f5c5b3"
                radius={[3, 3, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}
